import {
  DEFAULT_CACHE_CRITERIAS,
  DEFAULT_BATCH_CRITERIAS,
  CACHE_TYPES,
  REQUEST_TYPE,
  CacheCriteria,
  BatchCriteria,
  UrlConfig,
  ProxyCallback
} from './constants';
import urlService from '../../services/urlService';
import { httpClient }  from '../../http/http';
import localStorageService from '../../services/localStorageService/localStorageService';

class DataProxy {
  private callbacks: Map<string, Set<ProxyCallback>>;
  private dataStore: Map<string, object>;
  private pendingCalls: Map<string, boolean>;
  public readonly CacheType = CACHE_TYPES;
  public readonly RequestType = REQUEST_TYPE;

  constructor() {
    this.callbacks = new Map();
    this.dataStore = new Map();
    this.pendingCalls = new Map();
  }

  private tryFetchFromCache(resourceKey: string, cacheCriteria: CacheCriteria) {
    const { expirationWindowsMS, cacheType } = cacheCriteria;
    let cache;
    if (cacheType === CACHE_TYPES.LocalStorage) {
      cache = localStorageService.fetchNonExpiredCachedData(resourceKey, expirationWindowsMS);
    }
    return cache ? cache.data : null;
  }

  private handleError(resourceKey: string, error: object) {
    if (this.callbacks.has(resourceKey)) {
      this.callbacks.get(resourceKey).forEach(func => {
        func(null, error);
      });
      this.callbacks.delete(resourceKey);
    }
    this.pendingCalls.delete(resourceKey);
  }

  private handleCallback(resourceKey: string, data: object, isCacheEnabled: boolean) {
    this.dataStore.set(resourceKey, data);
    if (this.callbacks.has(resourceKey)) {
      this.callbacks.get(resourceKey).forEach(func => {
        func(data);
      });
      // For now, clear callbacks once called
      this.callbacks.delete(resourceKey);
    }

    if (isCacheEnabled) {
      localStorageService.saveDataByTimeStamp(resourceKey, data);
    }

    this.pendingCalls.delete(resourceKey);
  }

  private batchFromNetwork(urlConfig: UrlConfig, batchCriteria: BatchCriteria) {
    const { limit, key } = batchCriteria;
    const { data, params, method } = urlConfig;
    const promises = [];
    let startIndex = 0;
    let batchData = method === this.RequestType.Get ? params[key] : data[key];
    let subArray = batchData.slice(startIndex, limit);
    while (subArray.length > 0) {
      const urlConfigCopy = Object.assign({}, urlConfig);
      if (method === this.RequestType.Get) {
        const params = Object.assign({}, urlConfigCopy.params);
        params[key] = subArray.slice();
        urlConfigCopy.params = data;
      } else {
        const data = Object.assign({}, urlConfigCopy.data);
        data[key] = subArray.slice();
        urlConfigCopy.data = data;
      }

      promises.push(httpClient.request(urlConfigCopy));
      startIndex++;
      subArray = batchData.slice(startIndex * limit, startIndex * limit + limit);
    }
    return Promise.all(promises);
  }

  private fetchFromNetwork(
    urlConfig: UrlConfig,
    cacheCriteria: CacheCriteria,
    batchCriteria?: BatchCriteria
  ) {
    const { isCacheEnabled } = cacheCriteria;
    const { isBatchEnabled } = batchCriteria;
    const resourceKey = this.buildResourceKey(urlConfig);
    this.pendingCalls.set(resourceKey, true);
    if (isBatchEnabled) {
      return this.batchFromNetwork(urlConfig, batchCriteria)
        .then((data: object) => {
          this.handleCallback(resourceKey, data, isCacheEnabled);
          return data;
        })
        .catch((error: object) => {
          this.handleError(resourceKey, { error });
        });
    }
    return httpClient
      .request(urlConfig)
      .then((data: object) => {
        this.handleCallback(resourceKey, data, isCacheEnabled);
        return data;
      })
      .catch((error: object) => {
        this.handleError(resourceKey, { error });
      });
  }

  private fetchFromCache(urlConfig: UrlConfig, cacheCriteria: CacheCriteria) {
    const resourceKey = this.buildResourceKey(urlConfig);
    return new Promise(resolve => {
      resolve(
        this.dataStore.get(resourceKey) || this.tryFetchFromCache(resourceKey, cacheCriteria)
      );
    });
  }

  private buildResourceKey(urlConfig: UrlConfig): string {
    const { url, params, method, data } = urlConfig;
    return `${method}${url}${urlService.composeQueryString(params)}${urlService.composeQueryString(
      data
    )}`;
  }

  private buildCacheCriteria(cacheCriteria: CacheCriteria): CacheCriteria {
    return Object.assign(DEFAULT_CACHE_CRITERIAS, cacheCriteria);
  }

  public subscribe(
    callback: ProxyCallback,
    urlConfig: UrlConfig,
    cacheCriteria: CacheCriteria = DEFAULT_CACHE_CRITERIAS,
    batchCriteria: BatchCriteria = DEFAULT_BATCH_CRITERIAS
  ) {
    cacheCriteria = this.buildCacheCriteria(cacheCriteria);
    const isValidCallback = typeof callback === 'function';
    const { isCacheEnabled } = cacheCriteria;
    const resourceKey = this.buildResourceKey(urlConfig);
    const hasPendingCall = this.pendingCalls.has(resourceKey);

    this.fetchFromCache(urlConfig, cacheCriteria).then((cacheData: object) => {
      if (isCacheEnabled && cacheData && isValidCallback) {
        callback(cacheData);
      }

      if ((!cacheData && !hasPendingCall) || !isCacheEnabled) {
        if (isValidCallback) {
          if (hasPendingCall) {
            this.callbacks.get(resourceKey).add(callback);
          } else {
            this.callbacks.set(resourceKey, new Set([callback]));
          }
        }
        this.fetchFromNetwork(urlConfig, cacheCriteria, batchCriteria);
      }
    });

    return this.getUnsubscribeMethod(resourceKey, callback);
  }

  private getUnsubscribeMethod(resourceKey: string, callback: ProxyCallback) {
    return () => {
      if (this.callbacks.get(resourceKey)) {
        this.callbacks.get(resourceKey).delete(callback);
      }
    };
  }

  public refresh(
    urlConfig: UrlConfig,
    cacheCriteria: CacheCriteria = DEFAULT_CACHE_CRITERIAS,
    batchCriteria: BatchCriteria = DEFAULT_BATCH_CRITERIAS
  ) {
    return this.fetchFromNetwork(urlConfig, this.buildCacheCriteria(cacheCriteria), batchCriteria);
  }
}

const dataProxy = new DataProxy();

export default dataProxy;
